import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import axios from "axios";
import CustomTable from "../Utils/Tables/CustomTable";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import * as snackbarActions from "../../actions/snackbarActions";
import { config } from "../../Helpers/env";
import { LoadingGif } from "../Utils/Loader";
import MainSnackbar from "../Utils/Snackbar/SmsSnackbar";
import PageTitle from "../Utils/smsTitle";
import { SubHeader } from "../../Containers/SubHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPills, faEye, faFileExcel, faFilePdf } from "@fortawesome/free-solid-svg-icons";
import { BackButton, DataExportButton } from "../Utils/Buttons/DataExportationButton";
import { formatDate } from "../Utils/ConvertDate";
import { Container } from "../Utils/styledComponents";
import { Link } from "react-router-dom";

const getVisitLink = (row) => {
  if (row.payment_type === "INSURANCE") {
    return `/invoice-details/${row.visit_id}/${row.provider_id || 0}/${row.claim_status || 0}/${row.provider_type || "private"}`;
  }
  if (row.visit_type === "1") return `/billingreceipt/${row.visit_id}`;
  if (row.visit_type === "2") {
    const admissionId = row.patient_admission_id?.trim() || row.visit_id;
    return `/viewinpatientbill/${row.visit_id}/${admissionId}/1`;
  }
  return `/billingreceipt/${row.visit_id}`;
};

const LinkButton = ({ to, children }) => (
  <Link to={to}>
    <button className="btn btn-link text-primary p-0 small fw-bold" style={{ textDecoration: "underline" }}>
      {children}
    </button>
  </Link>
);

const headData = [
  { id: "sl", label: "SL" },
  { id: "sale_date", label: "Date" },
  { id: "visit_id", label: "Visit" },
  { id: "patient_name", label: "Patient" },
  { id: "product_name", label: "Product" },
  { id: "quantity", label: "Qty" },
  { id: "sale_usd", label: "Sale" },
  { id: "total_cost_usd", label: "Cost" },
  { id: "profit_usd", label: "Profit" }
];

const MedicineProfitReport = ({ actions, snackbars }) => {
  const [dateRange, setDateRange] = useState({
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date().toISOString().slice(0, 10)
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ data: [], summary: {}, period: {}, pagination: {} });
  const [activePage, setActivePage] = useState(1);
  const perPage = 10;

  const { start_date, end_date } = dateRange;
  const { data: rows = [], summary = {}, period = {}, pagination = {} } = data;

  const fetchReport = async (page = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(`${config.smsUrl}/creport/sales_profit_loss`, {
        params: { start_date, end_date, search: searchTerm || undefined, page, per_page: perPage }
      });
      setData(res.data || {});
    } catch (err) {
      actions.snackbarActions.errorSnackbar?.("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setActivePage(1);
  }, [start_date, end_date, searchTerm]);

  useEffect(() => {
    fetchReport(activePage);
  }, [start_date, end_date, searchTerm, activePage]);

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({ ...prev, [name]: value }));
  };

  const exportExcel = () => {
    const url = `${config.smsUrl}/creport/sales_profit_loss_export?format=excel&start_date=${start_date}&end_date=${end_date}&search=${encodeURIComponent(searchTerm)}&page=${activePage}&per_page=${perPage}`;
    window.open(url);
  };

  const exportPdf = () => {
    const url = `${config.smsUrl}/creport/sales_profit_loss_export?format=pdf&start_date=${start_date}&end_date=${end_date}&search=${encodeURIComponent(searchTerm)}&page=${activePage}&per_page=${perPage}`;
    window.open(url);
  };

  return (
    <Container className="mt-1">
      <PageTitle title="Medicine Sales & Profit Report" />
      <SubHeader title="Reports" subTitle="Medicine Profit & Loss">
        <FontAwesomeIcon icon={faPills} />
      </SubHeader>

      <MainSnackbar
        open={snackbars.openBar}
        variant={snackbars.type}
        message={snackbars.message}
        handleCloseBar={actions.snackbarActions.hideSnackbar}
      />

      <div style={{ margin: "10px" }}>
        <BackButton to="/reports/generalreports" text="Reports" />
      </div>

      {/* Filter Bar */}
      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body py-2 px-4 row g-2 align-items-center">
          <div className="col-auto">
            <input type="date" name="start_date" value={start_date} onChange={handleDateChange} className="form-control form-control-sm" />
          </div>
          <div className="col-auto">
            <input type="date" name="end_date" value={end_date} onChange={handleDateChange} className="form-control form-control-sm" />
          </div>
          <div className="col-auto">
            <input type="text" placeholder="Search patient, product, visit..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="form-control form-control-sm" />
          </div>
          <div className="col-auto">
            <button onClick={() => fetchReport(activePage)} disabled={loading} className="btn btn-primary btn-sm">Refresh</button>
            <button onClick={exportExcel} className="btn btn-success btn-sm ms-1"><FontAwesomeIcon icon={faFileExcel} /> Excel</button>
            <button onClick={exportPdf} className="btn btn-danger btn-sm ms-1"><FontAwesomeIcon icon={faFilePdf} /> PDF</button>
          </div>
        </div>
      </div>

      {/* SUMMARY CARDS */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm text-white" style={{ background: "linear-gradient(135deg, #28a745, #20c997)" }}>
            <div className="card-body text-center py-4">
              <h6 className="mb-2 opacity-75">Total Sales</h6>
              <h3 className="mb-0 fw-bold">${Number(summary.total_sales_usd || 0).toLocaleString()}</h3>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm text-white" style={{ background: "linear-gradient(135deg, #dc3545, #fd7e14)" }}>
            <div className="card-body text-center py-4">
              <h6 className="mb-2 opacity-75">Total Cost</h6>
              <h3 className="mb-0 fw-bold">${Number(summary.total_cost_usd || 0).toLocaleString()}</h3>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm text-white" style={{ background: "linear-gradient(135deg, #007bff, #6610f2)" }}>
            <div className="card-body text-center py-4">
              <h6 className="mb-2 opacity-75">NET PROFIT</h6>
              <h3 className="mb-0 fw-bold">${Number(summary.net_profit_usd || 0).toLocaleString()}</h3>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm bg-warning text-dark">
            <div className="card-body text-center py-4">
              <h6 className="mb-2">Profit Margin</h6>
              <h3 className="mb-0 fw-bold">{summary.profit_margin_percent || 0}%</h3>
            </div>
          </div>
        </div>
      </div>

      {/* TABLE */}
      <CustomTable
        title=""
        headData={headData}
        data={rows}
        total_count={pagination.total_records || 0}
        records={perPage}
        activePage={activePage}
        handleChangeNextPage={setActivePage}
        colSpan={headData.length}
        customPage
      >
        {loading ? (
          <TableBody>
            <TableRow>
              <TableCell colSpan={headData.length} align="center"><LoadingGif /></TableCell>
            </TableRow>
          </TableBody>
        ) : rows.length > 0 ? (
          <TableBody>
            {rows.map((row, idx) => {
              const cnt = (activePage - 1) * perPage + idx + 1;
              return (
                <TableRow key={cnt} hover>
                  <TableCell>{cnt}</TableCell>
                  <TableCell>{formatDate(row.sale_date)}</TableCell>
                  <TableCell><LinkButton to={getVisitLink(row)}>{row.visit_id}</LinkButton></TableCell>
                  <TableCell>{row.patient_name || "-"}</TableCell>
                  <TableCell>{row.product_name || "-"}</TableCell>
                  <TableCell className="text-center">{row.quantity}</TableCell>
                  <TableCell className="text-end">${row.sale_usd?.toFixed(2)}</TableCell>
                  <TableCell className="text-end">${row.total_cost_usd?.toFixed(2)}</TableCell>
                  <TableCell className="text-end fw-bold">${row.profit_usd?.toFixed(2)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        ) : (
          <TableBody>
            <TableRow>
              <TableCell colSpan={headData.length} align="center">No records found</TableCell>
            </TableRow>
          </TableBody>
        )}
      </CustomTable>
    </Container>
  );
};

const mapStateToProps = state => ({ snackbars: state.snackbar });
const mapDispatchToProps = dispatch => ({
  actions: { snackbarActions: bindActionCreators(snackbarActions, dispatch) }
});

export default connect(mapStateToProps, mapDispatchToProps)(MedicineProfitReport);
